/**
 * Resuming after an interruption (ENG-019): a phone call, Siri, an alarm or
 * another app's audio pauses the book without the listener asking, and the
 * book should carry on once it is over, as Flutter's `audio_session` does.
 *
 * A web app gets no "interruption ended" callback of its own, so this tracks
 * the pause the system made and decides, from the signals a browser does give,
 * when playing again is what the listener expects:
 *
 * - the browser resumes the element itself (Chrome on Android after a call;
 *   WebKit when iOS says the interrupted audio should resume): the engine
 *   adopts that playback;
 * - `navigator.audioSession` (where the browser has its `state`) leaves
 *   `interrupted`;
 * - the interruption took the screen (the page was showing at the pause and
 *   hid soon after, as an answered call does) and the page shows again: iOS
 *   returns to the app when the call ends.
 *
 * Nothing resumes after `INTERRUPTION_RESUME_WINDOW_MS`, after a pause the
 * listener asked for, or when the audio output changed at the pause
 * (headphones or a car's Bluetooth disconnected): playing out of the phone's
 * speaker then would surprise the listener.
 *
 * Pure state and rules; the engine feeds it events and acts on its answers.
 */

/** How long after an interruption the book may still resume on its own. */
export const INTERRUPTION_RESUME_WINDOW_MS = 60 * 60_000;
/** An interruption that hides the page (an answered call) does so within this long of the pause. */
export const INTERRUPTION_TOOK_SCREEN_MS = 60_000;
/** An output change (headphones, Bluetooth) this close to the pause explains it: never resume. */
export const ROUTE_CHANGE_GRACE_MS = 2_000;

export type ResumeSignal = 'browser' | 'audioSession' | 'visible';

interface Interruption {
  itemId: string;
  at: number;
  /** The page was showing at the pause and the interruption hid it (an answered call). */
  tookScreen: boolean;
  /** The page was showing when the book paused. */
  visibleAtPause: boolean;
}

export class InterruptionTracker {
  private current: Interruption | null = null;
  private lastRouteChangeAt = -Infinity;

  /** The book paused while the listener wanted it playing, and not for an error, a file change or the end. */
  begin(itemId: string, now: number, visible: boolean) {
    if (now - this.lastRouteChangeAt <= ROUTE_CHANGE_GRACE_MS) {
      this.current = null;
      return;
    }
    this.current = { itemId, at: now, tookScreen: false, visibleAtPause: visible };
  }

  /** The listener (or the engine on their behalf: pause, play, Stop, another book, sleep timer) took over. */
  clear() {
    this.current = null;
  }

  /** The audio output changed (`navigator.mediaDevices` `devicechange`). */
  routeChanged(now: number) {
    this.lastRouteChangeAt = now;
    if (this.current && now - this.current.at <= ROUTE_CHANGE_GRACE_MS) this.current = null;
  }

  /** The page hid. */
  hidden(now: number) {
    const c = this.current;
    if (c && c.visibleAtPause && now - c.at <= INTERRUPTION_TOOK_SCREEN_MS) c.tookScreen = true;
  }

  /** An interruption of `itemId` is pending and still recent. */
  pending(itemId: string | null, now: number): boolean {
    const c = this.current;
    if (!c) return false;
    if (now - c.at > INTERRUPTION_RESUME_WINDOW_MS) {
      this.current = null;
      return false;
    }
    return c.itemId === itemId;
  }

  /**
   * Whether `signal` means the interruption of `itemId` is over and playback
   * should resume. A true answer consumes the interruption.
   */
  shouldResume(signal: ResumeSignal, itemId: string | null, now: number): boolean {
    if (!this.pending(itemId, now)) return false;
    if (signal === 'visible' && !this.current!.tookScreen) return false;
    this.current = null;
    return true;
  }
}

/** `navigator.audioSession` where the browser reports its state (Safari, behind the Audio Session API). */
export interface AudioSessionLike extends EventTarget {
  state?: 'active' | 'inactive' | 'interrupted' | string;
}

export function audioSessionOf(nav: Navigator | undefined): AudioSessionLike | null {
  const s = (nav as (Navigator & { audioSession?: AudioSessionLike }) | undefined)?.audioSession;
  return s && typeof s.addEventListener === 'function' && typeof s.state === 'string' ? s : null;
}
